import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const WorkspaceTabsContext = createContext(null);

const NON_WORKSPACE_PATHS = new Set(["/"]);
// Sales-order tabs stay mounted (kept alive, just hidden) while open, and each
// one holds 1 long-lived SSE connection (see ReverseSyncStatusPanel), plus 1
// more held globally by the header's messaging stream. The production
// backend is plain HTTP (no TLS => no HTTP/2), so the browser caps concurrent
// connections per origin at 6 (Chrome/Firefox default for HTTP/1.1). This
// limit must stay comfortably under that budget: 1 (header) + N (tabs) <= 6.
// It used to be 2 when each tab opened 4 separate SSE connections instead of
// 1 - see WORKSPACE_TABS_ARCHITECTURE.md / SYNC_ARCHITECTURE.md for context.
const SALES_ORDER_DOCUMENT_TABS_LIMIT = 4;

const isSalesOrderDocumentPath = (path) => {
  if (!path) {
    return false;
  }
  return /^\/legacy\/sales-orders\/[^/]+\/[^/]+$/i.test(path)
    || /^\/legacy\/sales-orders\/document\/[^/]+$/i.test(path);
};

const getDefaultTitleFromPath = (path) => {
  if (!path || path === "/") {
    return "Inicio";
  }
  const segment = path.split("/").filter(Boolean).pop() || path;
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildUserSignature = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) {
      return "";
    }
    const user = JSON.parse(raw);
    const userId = user?.id ?? user?.userId ?? user?.username ?? user?.name ?? "";
    const tenant = localStorage.getItem("selectedTenantId")
      || localStorage.getItem("selectedTenant")
      || "";
    return `${String(userId)}::${String(tenant)}`;
  } catch (_error) {
    return "";
  }
};

export const WorkspaceTabsProvider = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [activePath, setActivePath] = useState("");
  const [refreshVersionByPath, setRefreshVersionByPath] = useState({});
  const [tabLimitMessage, setTabLimitMessage] = useState("");
  const [userSignature, setUserSignature] = useState(buildUserSignature);
  const tabsRef = useRef([]);

  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  const resetWorkspaceTabs = useCallback(() => {
    setTabs([]);
    setActivePath("");
    setRefreshVersionByPath({});
    setTabLimitMessage("");
  }, []);

  const openTab = useCallback((path, title, icon) => {
    if (!path || NON_WORKSPACE_PATHS.has(path)) {
      return true;
    }

    const currentTabs = tabsRef.current;
    const existingTab = currentTabs.find((tab) => tab.path === path);
    if (!existingTab && isSalesOrderDocumentPath(path)) {
      const openSalesOrderTabs = currentTabs.filter((tab) => isSalesOrderDocumentPath(tab.path)).length;
      if (openSalesOrderTabs >= SALES_ORDER_DOCUMENT_TABS_LIMIT) {
        setTabLimitMessage(t("workspaceTabs.salesOrderLimit"));
        return false;
      }
    }

    setTabs((prev) => {
      const exists = prev.some((tab) => tab.path === path);
      if (exists) {
        if (!title && !icon) {
          return prev;
        }
        let changed = false;
        const next = prev.map((tab) => {
          if (tab.path !== path) {
            return tab;
          }
          const nextTitle = title || tab.title;
          const nextIcon = icon || tab.icon;
          if (nextTitle === tab.title && nextIcon === tab.icon) {
            return tab;
          }
          changed = true;
          return {
            ...tab,
            title: nextTitle,
            ...(nextIcon ? { icon: nextIcon } : {}),
          };
        });
        return changed ? next : prev;
      }
      return [...prev, { path, title: title || getDefaultTitleFromPath(path), ...(icon ? { icon } : {}) }];
    });
    setActivePath((prev) => (prev === path ? prev : path));
    return true;
  }, [t]);

  const closeTab = useCallback((path) => {
    setTabs((prev) => {
      const closingIndex = prev.findIndex((tab) => tab.path === path);
      if (closingIndex < 0) {
        return prev;
      }
      const nextTabs = prev.filter((tab) => tab.path !== path);

      setRefreshVersionByPath((prevRefresh) => {
        const { [path]: _removed, ...rest } = prevRefresh;
        return rest;
      });

      if (path === activePath) {
        const fallback =
          nextTabs[closingIndex - 1] ||
          nextTabs[closingIndex] ||
          nextTabs[nextTabs.length - 1];
        if (fallback?.path) {
          setActivePath(fallback.path);
          navigate(fallback.path);
        } else {
          setActivePath("");
          navigate("/dashboard");
        }
      }
      return nextTabs;
    });
  }, [activePath, navigate]);

  const activateTab = useCallback((path) => {
    if (!path) {
      return;
    }
    setActivePath(path);
    navigate(path);
  }, [navigate]);

  const refreshTab = useCallback((path) => {
    if (!path) {
      return;
    }
    setRefreshVersionByPath((prev) => ({
      ...prev,
      [path]: (prev[path] || 0) + 1,
    }));
  }, []);

  const refreshAllTabs = useCallback(() => {
    setRefreshVersionByPath((prev) => {
      const next = { ...prev };
      tabs.forEach((tab) => {
        next[tab.path] = (next[tab.path] || 0) + 1;
      });
      return next;
    });
  }, [tabs]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (NON_WORKSPACE_PATHS.has(currentPath)) {
      resetWorkspaceTabs();
      return;
    }
    const opened = openTab(currentPath);
    if (!opened) {
      const fallbackPath = activePath || "/dashboard";
      if (fallbackPath !== currentPath) {
        navigate(fallbackPath, { replace: true });
      }
      return;
    }
    setActivePath(currentPath);
  }, [activePath, location.pathname, navigate, openTab, resetWorkspaceTabs]);

  useEffect(() => {
    const handleUserDataChanged = () => {
      const next = buildUserSignature();
      if (next !== userSignature) {
        setUserSignature(next);
        resetWorkspaceTabs();
      }
    };

    window.addEventListener("userDataChanged", handleUserDataChanged);
    return () => window.removeEventListener("userDataChanged", handleUserDataChanged);
  }, [resetWorkspaceTabs, userSignature]);

  useEffect(() => {
    const handleExternalRefresh = (event) => {
      const targetPath = event?.detail?.path;
      if (targetPath) {
        refreshTab(targetPath);
        return;
      }
      refreshAllTabs();
    };

    window.addEventListener("idax:tabs:refresh", handleExternalRefresh);
    return () => window.removeEventListener("idax:tabs:refresh", handleExternalRefresh);
  }, [refreshAllTabs, refreshTab]);

  const getRenderKeyForPath = useCallback((path) => {
    const version = refreshVersionByPath[path] || 0;
    return String(version);
  }, [refreshVersionByPath]);

  const value = useMemo(() => ({
    tabs,
    activePath,
    openTab,
    closeTab,
    activateTab,
    refreshTab,
    refreshAllTabs,
    getRenderKeyForPath,
    tabLimitMessage,
    clearTabLimitMessage: () => setTabLimitMessage(""),
  }), [
    tabs,
    activePath,
    openTab,
    closeTab,
    activateTab,
    refreshTab,
    refreshAllTabs,
    getRenderKeyForPath,
    tabLimitMessage,
  ]);

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
};

export const useWorkspaceTabs = () => {
  const context = useContext(WorkspaceTabsContext);
  if (!context) {
    throw new Error("useWorkspaceTabs must be used within WorkspaceTabsProvider");
  }
  return context;
};

